<template>
  <div>
    <page-header :title="title" :breadcrumb="breadcrumb" :container-class="containerClass" />
    <div class="page-section">
      <div :class="containerClass">
        <page-separator :title="title" />

        <div class="card mb-0">
          <div class="card-header">
            <div class="row align-items-center" style="white-space: nowrap">
              <!-- Search -->
              <div class="col-lg-auto">
                <form class="search-form search-form--light d-lg-inline-flex mb-8pt mb-lg-0" @submit.prevent="">
                  <b-form-input
                    class="w-lg-auto"
                    placeholder="Search Competitions"
                    v-model="searchTerm"
                    @input="onSearch"
                  />
                  <b-btn variant="flush" type="submit">
                    <md-icon>search</md-icon>
                  </b-btn>
                </form>
              </div>

              <!-- Filters and Add Btn -->
              <div class="col-lg d-flex flex-wrap justify-content-end">
                <!-- Filter dropdowns -->
                <b-dropdown class="mr-8pt" :text="statusFilter" variant="link text-black-70" right>
                  <b-dropdown-item @click="statusFilter = 'All Status'; fetchCompetitions()">All Status</b-dropdown-item>
                  <b-dropdown-item @click="statusFilter = 'Active'; fetchCompetitions()">Active</b-dropdown-item>
                  <b-dropdown-item @click="statusFilter = 'Inactive'; fetchCompetitions()">Inactive</b-dropdown-item>
                </b-dropdown>

                <b-dropdown class="mr-8pt" :text="publishedFilter" variant="link text-black-70" right>
                  <b-dropdown-item @click="publishedFilter = 'All'; fetchCompetitions()">All</b-dropdown-item>
                  <b-dropdown-item @click="publishedFilter = 'Published'; fetchCompetitions()">Published</b-dropdown-item>
                  <b-dropdown-item @click="publishedFilter = 'Draft'; fetchCompetitions()">Draft</b-dropdown-item>
                </b-dropdown>

                <b-btn :to="{ name: 'sa-add-skills-competition' }" exact variant="primary">Add Competition</b-btn>
              </div>
            </div>
          </div>

          <!-- Competitions Table -->
          <b-table
            :fields="tableColumns"
            :items="competitions"
            :busy="isLoading"
            head-variant="light"
            class="table-nowrap sticky-lcol-table"
            hover
            responsive
            no-local-sorting
            @sort-changed="onSortChange"
          >
            <template #cell(title)="data">
              <div>
                <strong>{{ data.item.title }}</strong>
                <br>
                <small class="text-muted">{{ data.item.organization_name }}</small>
              </div>
            </template>

            <template #cell(image)="data">
              <fmv-avatar :title="true" title-class="bg-transparent" rounded no-link slot="aside">
                <b-img :src="data.item.image" class="img-fluid" width="40" alt="Competition Image" v-if="data.item.image" />
                <i class="fas fa-trophy fa-lg text-warning" v-else></i>
              </fmv-avatar>
            </template>

            <template #cell(dates)="data">
              <div>
                <small><strong>Registration:</strong><br>{{ formatDate(data.item.registration_start_date) }} - {{ formatDate(data.item.registration_end_date) }}</small><br>
                <small><strong>Competition:</strong><br>{{ formatDate(data.item.competition_start_date) }} - {{ formatDate(data.item.competition_end_date) }}</small>
              </div>
            </template>

            <template #cell(participation)="data">
              <a href="#" @click.prevent class="text-primary" :id="`schools-popover-${data.item.id}`"
                >{{ data.item.participating_schools_count }} School(s)</a
              >
              <schools-list-popover :schools="data.item.participating_schools" :target-id="data.item.id" />
            </template>

            <template #cell(status)="data">
              <div>
                <b-badge
                  :class="'border border-' + (data.item.is_active ? 'success text-success' : 'secondary text-secondary')"
                  variant="none"
                >
                  {{ data.item.is_active ? 'Active' : 'Inactive' }}
                </b-badge>
                <br>
                <b-badge
                  :class="'border border-' + (data.item.is_published ? 'info text-info' : 'warning text-warning')"
                  variant="none"
                >
                  {{ data.item.is_published ? 'Published' : 'Draft' }}
                </b-badge>
              </div>
            </template>

            <template #head(actions)="">
              <span></span>
            </template>
            <template #cell(actions)="data">
              <b-dropdown variant="link" toggle-class="text-decoration-none p-0" no-caret>
                <template #button-content>
                  <i class="material-icons icon-24pt text-info">more_vert</i>
                </template>
                <b-dropdown-item @click="toggleStatus(data.index)">
                  <md-icon :class="data.item.is_active ? 'text-danger mr-2' : 'text-success mr-2'">{{
                    data.item.is_active ? 'block' : 'check_circle_outline'
                  }}</md-icon>
                  {{ data.item.is_active ? 'Mark Inactive' : 'Mark Active' }}
                </b-dropdown-item>
                <b-dropdown-divider></b-dropdown-divider>
                <b-dropdown-item
                  :to="{
                    name: 'sa-edit-skills-competition',
                    params: { id: data.item.id },
                  }"
                >
                  <md-icon class="text-info mr-2">edit</md-icon>
                  {{ $t('generalMsgs.edit') }}
                </b-dropdown-item>
                <b-dropdown-divider></b-dropdown-divider>
                <b-dropdown-item @click="removeCompetition(data.item.id)">
                  <md-icon class="text-danger mr-2">delete</md-icon>
                  {{ $t('generalMsgs.remove') }}
                </b-dropdown-item>
              </b-dropdown>
            </template>
          </b-table>

          <!-- Footer Pagination -->
          <div class="card-footer">
            <pagination
              v-model="currentPage"
              :total-rows="totalCompetitions"
              :per-page="perPage"
              @change="onPageChange"
              aria-controls="competitions-table"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PageHeader from '@/components/Ui/PageHeader.vue';
import PageSeparator from '@/components/Ui/PageSeparator.vue';
import { FmvAvatar } from 'fmv-avatar';
import { debounce } from 'lodash';
import { mapActions } from 'vuex';

import Page from '@/components/Page.vue';
import Pagination from '../../../components/Ui/Pagination.vue';
import MdIcon from '../../../components/Ui/MdIcon.vue';
import SchoolsListPopover from './SchoolsListPopover.vue';
import { DEFAULT_PAGE_SIZE } from '../../../common/constants';

export default {
  components: { MdIcon, PageHeader, PageSeparator, Pagination, FmvAvatar, SchoolsListPopover },
  extends: Page,

  data() {
    return {
      title: 'Manage Skills Competitions',

      isLoading: false,
      perPage: DEFAULT_PAGE_SIZE,
      currentPage: 1,
      totalCompetitions: 0,
      competitions: [],

      searchTerm: '',
      statusFilter: 'All Status',
      publishedFilter: 'All',
      ordering: '-created_at',
    };
  },

  computed: {
    breadcrumb() {
      return [
        { text: this.$t('home'), to: this.routes.home },
        { text: 'Skills Competitions', active: true },
      ];
    },

    tableColumns() {
      return [
        {
          key: 'actions',
          label: '',
          class: 'text-left',
          thStyle: { minWidth: '120px' },
        },
        { key: 'image', label: '', thStyle: { width: '60px' } },
        { key: 'title', label: 'Competition' },
        { key: 'dates', label: 'Important Dates' },
        { key: 'participation', label: 'Participation', tdClass: 'clickable-item' },
        { key: 'status', label: 'Status' },
      ];
    },
  },

  mounted() {
    this.fetchCompetitions();
  },

  methods: {
    ...mapActions('skillsCompetitions', ['getAllCompetitions', 'updateIsActive', 'deleteCompetition']),

    onSearch: debounce(function () {
      this.currentPage = 1;
      this.fetchCompetitions();
    }, 500),

    onSortChange(sortBy) {
      this.ordering = sortBy.sortDesc ? `-${sortBy.sortBy}` : sortBy.sortBy;
      this.fetchCompetitions();
    },

    onPageChange(pageNum) {
      this.currentPage = pageNum;
      this.fetchCompetitions();
    },
    async removeCompetition(id) {
      try {
        const isConfirmed = await this.$bvModal.msgBoxConfirm(
          `Please confirm that you want to remove the selected competition. The skill competition will no longer be available.`,
          {
            title: 'Are you sure?',
            size: 'md',
            buttonSize: 'sm',
            okVariant: 'danger',
            okTitle: 'Yes',
            cancelTitle: 'No',
            footerClass: 'p-2',
            hideHeaderClose: false,
            centered: true,
          },
        );
        if (isConfirmed) {
          this.isLoading = true;
          await this.deleteCompetition(id);
          this.fetchCompetitions();
          this.makeToast({ variant: 'success', msg: 'Competition removed successfully!' });
          this.isLoading = false;
        }
      } catch (error) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
        this.isLoading = false;
      }
    },
    async fetchCompetitions() {
      this.isLoading = true;
      document.getElementById('app').scrollIntoView();

      try {
        const params = {
          limit: this.perPage,
          offset: (this.currentPage - 1) * this.perPage,
          ordering: this.ordering,
          include_schools: true, // Get school details for popover
        };

        if (this.searchTerm) {
          params.search = this.searchTerm;
        }

        if (this.statusFilter !== 'All Status') {
          params.is_active = this.statusFilter === 'Active';
        }

        if (this.publishedFilter !== 'All') {
          params.is_published = this.publishedFilter === 'Published';
        }

        const response = await this.getAllCompetitions(params);
        const data = response.data?.results || response.results || [];
        this.competitions = Array.isArray(data) ? data : [];
        this.totalCompetitions = response.data?.count || response.count || this.competitions.length;
      } catch (error) {
        if (error.response?.status === 401 || error.response?.status === 403) {
          this.makeToast({ 
            variant: 'warning', 
            msg: 'Please login as SuperAdmin to view competitions' 
          });
          this.$router.push('/auth/login');
        } else {
          this.makeToast({ variant: 'danger', msg: 'Error loading competitions' });
        }
      } finally {
        this.isLoading = false;
      }
    },

    async toggleStatus(index) {
      const currStatus = this.competitions[index].is_active;
      try {
        const resp = await this.updateIsActive({ id: this.competitions[index].id, is_active: !currStatus });
        if (resp.status_code === 200) {
          this.makeToast({ variant: 'success', msg: `Competition marked as ${currStatus ? 'Inactive' : 'Active'}` });
          this.fetchCompetitions();
        }
      } catch (error) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
    },



    formatDate(dateString) {
      if (!dateString) return '';
      return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    },
  },
};
</script>

<style scoped>
/* Make table responsive on mobile */
@media (max-width: 576px) {
  .table-responsive {
    border: none;
  }
  
  /* Hide less important columns on mobile */
  .table th:nth-child(4),
  .table td:nth-child(4) {
    display: none;
  }
  
  .table th:nth-child(5),
  .table td:nth-child(5) {
    display: none;
  }
}
</style>